<template>
  <div class="modal-backdrop">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <button type="button" class="close" @click="close" aria-label="Close">
            <i class="fa fa-times"></i>
          </button>
          <h5 class="modal-title">{{ isEdit ? 'Edit Embed' : 'Create Embed' }}</h5>
        </div>
        <div class="modal-body">
          <form @submit.prevent="save">
            <div class="form-group">
              <label for="embedId" class="form-label">Embed ID <span class="text-danger">*</span></label>
              <input
                type="text"
                id="embedId"
                v-model="form.embed_id"
                class="form-control"
                :disabled="isEdit"
                placeholder="unique-embed-id"
                required
              />
              <small v-if="isEdit" class="form-text text-muted">Embed ID cannot be changed after creation.</small>
            </div>

            <div class="form-group">
              <label for="name" class="form-label">Name <span class="text-danger">*</span></label>
              <small class="form-text text-muted">This is displayed to users as the name of the dashboard.</small>
              <input
                type="text"
                id="name"
                v-model="form.name"
                class="form-control"
                placeholder="Dashboard Name"
                required
              />
            </div>

            <!-- Internal User UUIDs: TypeAhead + chips -->
            <div class="form-group">
              <label class="form-label">Internal User UUIDs</label>
              <small class="form-text text-muted">
                Search for Goalbook Business users by name or email. Selected users can access this embed.
              </small>
              <div v-if="usersLoading" class="text-muted small mt-1">Loading users...</div>
              <div v-else class="autocomplete-wrapper">
                <input
                  type="text"
                  v-model="userSearchText"
                  class="form-control mb-1"
                  placeholder="Search by name or email..."
                  @focus="userDropdownOpen = true"
                  @blur="hideUserDropdown"
                  autocomplete="off"
                />
                <ul v-if="userDropdownOpen && filteredUsers.length > 0" class="autocomplete-dropdown">
                  <li
                    v-for="user in filteredUsers"
                    :key="user.uuid"
                    @mousedown.prevent="addUser(user)"
                    class="autocomplete-item"
                  >
                    <strong>{{ user.name }}</strong>
                    <span class="text-muted"> - {{ user.email }}</span>
                  </li>
                </ul>
                <div class="selected-chips" aria-label="Selected users">
                  <span
                    v-for="user in selectedUsers"
                    :key="user.uuid"
                    class="badge badge-primary selected-chip"
                  >
                    {{ user.name || user.email || user.uuid }}
                    <button
                      type="button"
                      class="chip-remove"
                      @click="removeUser(user)"
                      :aria-label="'Remove ' + (user.name || user.uuid)"
                    ><i class="fa fa-times"></i></button>
                  </span>
                  <span v-if="selectedUsers.length === 0" class="text-muted small">No users selected.</span>
                </div>
              </div>
            </div>

            <!-- Domain UUIDs: TypeAhead + chips -->
            <div class="form-group">
              <label class="form-label">Domain UUIDs</label>
              <small class="form-text text-muted">
                Search for domains by name. Selected domains will have access to this embed.
              </small>
              <div v-if="domainsLoading" class="text-muted small mt-1">Loading domains...</div>
              <div v-else class="autocomplete-wrapper">
                <input
                  type="text"
                  v-model="domainSearchText"
                  class="form-control mb-1"
                  placeholder="Search by domain name..."
                  @focus="domainDropdownOpen = true"
                  @blur="hideDomainDropdown"
                  autocomplete="off"
                />
                <ul v-if="domainDropdownOpen && filteredDomains.length > 0" class="autocomplete-dropdown">
                  <li
                    v-for="domain in filteredDomains"
                    :key="domain.uuid"
                    @mousedown.prevent="addDomain(domain)"
                    class="autocomplete-item"
                  >
                    <strong>{{ domain.name }}</strong>
                  </li>
                </ul>
                <div class="selected-chips" aria-label="Selected domains">
                  <span
                    v-for="domain in selectedDomains"
                    :key="domain.uuid"
                    class="badge badge-info selected-chip"
                  >
                    {{ domain.name || domain.uuid }}
                    <button
                      type="button"
                      class="chip-remove"
                      @click="removeDomain(domain)"
                      :aria-label="'Remove ' + (domain.name || domain.uuid)"
                    ><i class="fa fa-times"></i></button>
                  </span>
                  <span v-if="selectedDomains.length === 0" class="text-muted small">No domains selected.</span>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label for="schoolFieldName" class="form-label">School Field Name</label>
              <input
                type="text"
                id="schoolFieldName"
                v-model="form.school_field_name"
                class="form-control"
                placeholder="Optional field name"
              />
              <small class="form-text text-muted">If it's a custom board, sometimes it has a different field name for the school. We need to specify that here so the school filtering can work.</small>
            </div>

            <div class="form-group">
              <div class="checkbox">
                <label>
                  <input type="checkbox" id="active" v-model="form.active" />
                  Active
                </label>
              </div>
              <small class="form-text text-muted">Inactive embeds are hidden from end users.</small>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-testid="cancel-button" @click="close">
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            data-testid="save-button"
            @click="save"
            :disabled="saving"
          >
            <i v-if="saving" class="fa fa-spinner fa-spin me-1"></i>
            {{ saving ? 'Saving...' : (isEdit ? 'Update' : 'Create') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Matches GBBusinessDomainUUID constant in goalbook-go-common/domo/models.go
const GB_BUSINESS_DOMAIN_UUID = '50d6b2db-1a9f-4c21-7aff-b296a3edd75a'

export default {
  name: 'DomoEmbedForm',
  props: {
    selectedEmbed: {
      type: Object,
      default: null,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    onSave: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      form: {
        embed_id: '',
        name: '',
        school_field_name: '',
        active: true,
      },
      // User autocomplete state
      allUsers: [],
      selectedUsers: [],
      userSearchText: '',
      usersLoading: false,
      // Domain autocomplete state
  allDomains: [],
      selectedDomains: [],
      domainSearchText: '',
      domainsLoading: false,
      userDropdownOpen: false,
      domainDropdownOpen: false,
      saving: false,
    }
  },
  computed: {
    filteredUsers() {
      const q = (this.userSearchText || '').toLowerCase().trim()
      if (!q) return []
      const selectedUuids = new Set(this.selectedUsers.map(u => u.uuid))
      return this.allUsers
        .filter(u => !selectedUuids.has(u.uuid))
        .filter(u => {
          const name = (u.name || '').toLowerCase()
          const email = (u.email || '').toLowerCase()
          return name.includes(q) || email.includes(q)
        })
        .slice(0, 10)
    },
    filteredDomains() {
      const q = (this.domainSearchText || '').toLowerCase().trim()
      if (!q) return []
      const selectedUuids = new Set(this.selectedDomains.map(d => d.uuid))
      return this.allDomains
        .filter(d => !selectedUuids.has(d.uuid))
        .filter(d => (d.name || '').toLowerCase().includes(q))
        .slice(0, 10)
    },
  },
  watch: {
    selectedEmbed: {
      handler() {
        this.resetFormFromProps()
        if (this.isEdit && this.selectedEmbed && this.allUsers.length > 0) {
          this.populateSelectionsFromEmbed()
        }
      },
    },
  },
  async mounted() {
    this.resetFormFromProps()
    await Promise.all([this.fetchGoalbookBusinessUsers(), this.fetchAllDomains()])

    if (this.isEdit && this.selectedEmbed) {
      this.populateSelectionsFromEmbed()
    }
  },
  methods: {
    resetFormFromProps() {
      if (this.isEdit && this.selectedEmbed) {
        this.form = {
          embed_id: this.selectedEmbed.embed_id || '',
          name: this.selectedEmbed.name || '',
          school_field_name: this.selectedEmbed.school_field_name || '',
          active: this.selectedEmbed.active !== false,
        }
      } else {
        this.form = {
          embed_id: '',
          name: '',
          school_field_name: '',
          active: true,
        }
        this.selectedUsers = []
        this.selectedDomains = []
      }
    },
    async fetchGoalbookBusinessUsers() {
      this.usersLoading = true
      try {
        const { userCache } = this.$angularWorld
        const users = await userCache.fetchNormalizedUsers(GB_BUSINESS_DOMAIN_UUID)
        this.allUsers = (users || []).map(u => ({
          uuid: u.uuid,
          name: u.name || '',
          email: u.email || '',
          displayLabel: u.name ? `${u.name} (${u.email})` : u.email,
        }))
      } catch (err) {
        console.error('Failed to load Goalbook Business users:', err)
      } finally {
        this.usersLoading = false
      }
    },
    parseCommaSeparated(value) {
      if (!value || !value.trim()) return [];
      return value
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    },

    populateSelectionsFromEmbed() {
      const userUuids = this.selectedEmbed.internal_user_uuids || []
      const domainUuids = this.selectedEmbed.domain_uuids || []

      // Resolve user UUIDs to name/email using the loaded allUsers list
      this.selectedUsers = userUuids.map(uuid => {
        const found = this.allUsers.find(u => u.uuid === uuid)
        return found || { uuid, name: '', email: '' }
      })

      // For domains, start with UUID-only placeholders, then resolve names
      this.selectedDomains = domainUuids.map(uuid => ({ uuid, name: uuid }))
      this.resolveDomainNames(domainUuids)
    },

    resolveDomainNames(uuids) {
      this.selectedDomains = uuids.map(uuid => {
        const found = this.allDomains.find(d => d.uuid === uuid)
        return found ? { uuid, name: found.name } : { uuid, name: uuid }
      })
    },

    addUser(user) {
      if (!user || !user.uuid) return
      if (this.selectedUsers.some(u => u.uuid === user.uuid)) return
      this.selectedUsers = [...this.selectedUsers, user]
      this.userSearchText = ''
      this.userDropdownOpen = false
    },
    removeUser(user) {
      this.selectedUsers = this.selectedUsers.filter(u => u.uuid !== user.uuid)
    },

    async fetchAllDomains() {
      this.domainsLoading = true
      try {
        const { graph } = this.$angularWorld
        const domains = await graph.getDomains()
        this.allDomains = (Array.isArray(domains) ? domains : []).map(d => ({
          uuid: d.uuid,
          name: d.name || '',
        }))
      } catch (err) {
        console.error('Failed to load domains:', err)
      } finally {
        this.domainsLoading = false
      }
    },

    addDomain(domain) {
      if (!domain || !domain.uuid) return
      if (this.selectedDomains.some(d => d.uuid === domain.uuid)) return
      this.selectedDomains = [...this.selectedDomains, domain]
      this.domainSearchText = ''
    },

    removeDomain(domain) {
      this.selectedDomains = this.selectedDomains.filter(d => d.uuid !== domain.uuid)
    },

    hideUserDropdown() {
      setTimeout(() => { this.userDropdownOpen = false }, 150)
    },
 
    hideDomainDropdown() {
      setTimeout(() => { this.domainDropdownOpen = false }, 150)
    },
 
    close() {
      this.$emit('close')
    },

    async save() {
      if (this.saving) return;
      const embedId = this.form.embed_id.trim();
      const name = this.form.name.trim();
      if (!embedId || !name) {
        return;
      }
      const payload = {
        ...this.form,
        embed_id: embedId,
        name,
        internal_user_uuids: this.selectedUsers.map(u => u.uuid),
        domain_uuids: this.selectedDomains.map(d => d.uuid),
      }
      this.saving = true
      try {
        if (this.onSave) {
          await this.onSave(payload)
        } else {
          this.$emit('save', payload)
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.form-group {
  margin-bottom: 1rem;
}

.selected-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  min-height: 2rem;
  align-items: center;
}

.selected-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.85rem;
  padding: 0.3rem 0.5rem;
}

.chip-remove {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  opacity: 0.7;
}

.chip-remove:hover {
  opacity: 1;
}

.autocomplete-wrapper {
  position: relative;
}
 
.autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  list-style: none;
  margin: 0;
  padding: 0;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  max-height: 240px;
  overflow-y: auto;
}
 
.autocomplete-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
}
 
.autocomplete-item:hover {
  background-color: #f5f5f5;
}
</style>